import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Hotel, Users, Phone, Mail, ArrowLeft, Clock, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import translations from '../contexts/translations';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext'; // Import du contexte d'authentification

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nouzhaback-theta.vercel.app';

const ReservationSuccessPage = ({ language }) => {
  const t = translations[language];
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth(); // Récupération de l'état d'authentification
  
  // Récupérer le sessionId depuis les paramètres d'URL
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Fonction pour récupérer les informations de la session Stripe
    const fetchSessionInfo = async () => {
      if (!sessionId) {
        setError("ID de session manquant");
        setLoading(false);
        return;
      }

      try {
        // Utiliser l'URL correcte pour l'API
        const response = await axios.get(`${API_BASE_URL}/stripe/checkout-session/${sessionId}`);
        
        const sessionInfo = response.data;
        
        // Extraire les métadonnées de la session
        const metadata = sessionInfo.metadata || {};
        
        // Récupération optimisée de l'email client
        // 1. D'abord essayer depuis customer_details si disponible
        // 2. Sinon depuis customer_email (configuré à la création de la session)
        // 3. Sinon depuis les métadonnées (sauvegardé pour les utilisateurs non authentifiés)
        // 4. Enfin, valeur par défaut si rien n'est disponible
        const customerEmail = 
          sessionInfo.customer_details?.email || 
          sessionInfo.customer_email || 
          metadata.userEmail || 
          "Email non disponible";
        
        // Formatage optimisé du montant avec le symbole de devise approprié
        const currencySymbol = sessionInfo.currency === 'eur' ? '€' : '€';
        const formattedAmount = sessionInfo.amount_total 
          ? `${(sessionInfo.amount_total / 100).toFixed(2)} ${currencySymbol}`
          : "Montant non disponible";
        
        // Récupérer les IDs de réservation depuis différentes sources possibles
        // Essayer d'abord d'obtenir l'ID MongoDB depuis le backend
        console.log("Recherche de l'ID de réservation dans les métadonnées:", metadata);
        
        // Chercher l'ID de l'objet MongoDB de la réservation dans les différentes propriétés possibles
        let reservationObjectId = null;
        
        // Vérifier d'abord si on a une réservation d'hôtel ou de circuit existante
        if (metadata.type === 'hotel') {
          // Pour les réservations d'hôtel, essayer de récupérer la réservation par sessionId
          try {
            const reservationResponse = await axios.get(`${API_BASE_URL}/reservations/hotels/by-session/${sessionId}`);
            if (reservationResponse.data && reservationResponse.data._id) {
              reservationObjectId = reservationResponse.data._id;
              console.log("ID de réservation d'hôtel trouvé:", reservationObjectId);
            }
          } catch (err) {
            console.log("Aucune réservation d'hôtel trouvée par sessionId");
          }
        } else if (metadata.type === 'circuit') {
          // Pour les réservations de circuit, essayer de récupérer la réservation par sessionId
          try {
            const reservationResponse = await axios.get(`${API_BASE_URL}/reservations/circuits/by-session/${sessionId}`);
            if (reservationResponse.data && reservationResponse.data._id) {
              reservationObjectId = reservationResponse.data._id;
              console.log("ID de réservation de circuit trouvé:", reservationObjectId);
            }
          } catch (err) {
            console.log("Aucune réservation de circuit trouvée par sessionId");
          }
        }
        
        // Si on n'a pas trouvé l'ID via les API, chercher dans les métadonnées
        if (!reservationObjectId) {
          reservationObjectId = metadata.reservationId || metadata.idReservation || sessionId;
        }
        
        setReservation({
          id: sessionId,
          reservationObjectId: reservationObjectId, // Stocker l'ID de l'objet MongoDB séparément
          hotelName: metadata.hotelName || metadata.circuitTitle || "Réservation confirmée",
          dates: {
            arrival: metadata.dateArrivee || metadata.dateDebut || new Date().toLocaleDateString(),
            departure: metadata.dateDepart || metadata.dateFin || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()
          },
          personnes: metadata.personnes || metadata.nombrePersonnes || "1",
          amount: formattedAmount,
          customer: {
            name: sessionInfo.customer_details?.name || "Client",
            email: customerEmail
          },
          // Stockage du type de service pour la demande de facture
          serviceType: metadata.type || metadata.serviceType || (metadata.hotelName ? 'hotel' : metadata.circuitTitle ? 'circuit' : 'hotel')
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des informations de réservation :", error);
        toast.error("Impossible de récupérer les détails complets de votre réservation");
        setError("Impossible de récupérer les détails de votre réservation");
        
        // Créer des données de secours pour éviter une page vide
        setReservation({
          id: sessionId || "ID non disponible",
          hotelName: "Votre réservation",
          dates: {
            arrival: new Date().toLocaleDateString(),
            departure: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()
          },
          personnes: "1",
          amount: "Montant non disponible",
          customer: {
            name: "Client",
            email: "Email non disponible"
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSessionInfo();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-28">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails de votre réservation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-28 pb-16 px-6 sm:px-8 flex flex-col items-center justify-center">
      <div className="max-w-3xl w-full">
        {/* Bannière de confirmation */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-t-2xl p-8 text-white relative overflow-hidden">
          {/* Cercles décoratifs */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/10"></div>
          
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center">
            <div className="mb-4 sm:mb-0 sm:mr-6 flex justify-center sm:justify-start">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>
            <div className="text-center sm:text-left">
              <h1 className="text-3xl font-bold mb-2">{t.bookingConfirmed || 'Réservation confirmée!'}</h1>
              <p className="opacity-90">{t.thankYouMessage || 'Merci pour votre réservation. Votre confirmation a été envoyée par email.'}</p>
              
              {error && (
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-400/20 text-white border border-yellow-400/50">
                  {t.limitedDetails || 'Détails limités disponibles'}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Carte principale avec les détails */}
        <div className="bg-white rounded-b-2xl shadow-lg p-8 mb-6">
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center sm:text-left">{t.bookingDetails || 'Détails de la réservation'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex justify-center sm:justify-start mb-2 sm:mb-0 sm:mr-3">
                  <Hotel className="w-5 h-5 text-orange-500 flex-shrink-0" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-500">{t.accommodation || 'Hébergement'}</p>
                  <p className="font-medium text-gray-800">{reservation?.hotelName}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex justify-center sm:justify-start mb-2 sm:mb-0 sm:mr-3">
                  <Calendar className="w-5 h-5 text-orange-500 flex-shrink-0" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-500">{t.dates || 'Dates'}</p>
                  <p className="font-medium text-gray-800">
                    {reservation?.dates.arrival} - {reservation?.dates.departure}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex justify-center sm:justify-start mb-2 sm:mb-0 sm:mr-3">
                  <Users className="w-5 h-5 text-orange-500 flex-shrink-0" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-500">{t.guests || 'Personnes'}</p>
                  <p className="font-medium text-gray-800">{reservation?.personnes} {reservation?.personnes === "1" ? (t.person || 'personne') : (t.people || 'personnes')}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex justify-center sm:justify-start mb-2 sm:mb-0 sm:mr-3">
                  <Mail className="w-5 h-5 text-orange-500 flex-shrink-0" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-800">{reservation?.customer.email}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <p className="text-sm text-gray-500">{t.totalAmount || 'Montant total payé'}</p>
              <p className="text-2xl font-bold text-gray-800">{reservation?.amount}</p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-500">{t.bookingID || 'ID de réservation'}</p>
              <p className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{reservation?.id}</p>
            </div>
          </div>
        </div>
        
        {/* Options et prochaines étapes */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">{t.nextSteps || 'Prochaines étapes'}</h2>
          
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex justify-center sm:justify-start mb-3 sm:mb-0 sm:mr-4">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">1</div>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-medium text-gray-800">{t.checkEmail || 'Vérifiez votre email'}</h3>
                <p className="text-gray-600">{t.emailSummary || 'Un récapitulatif de votre réservation a été envoyé à votre adresse email.'}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex justify-center sm:justify-start mb-3 sm:mb-0 sm:mr-4">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">2</div>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-medium text-gray-800">{t.prepareDocuments || 'Préparez vos documents'}</h3>
                <p className="text-gray-600">{t.documentsInfo || 'Ayez votre pièce d\'identité et votre numéro de réservation à portée de main pour l\'enregistrement.'}</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex justify-center sm:justify-start mb-3 sm:mb-0 sm:mr-4">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">3</div>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-medium text-gray-800">{t.needAssistance || 'Besoin d\'assistance?'}</h3>
                <p className="text-gray-600">{t.supportAvailable || 'Notre équipe est disponible 24/7 pour vous aider avec votre réservation.'}</p>
                <div className="mt-4 flex flex-wrap gap-3 justify-center sm:justify-start">
                  <a href="tel:+212651913292" className="inline-flex items-center text-sm px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors">
                    <Phone className="w-4 h-4 mr-2" /> +212 651 913 292
                  </a>
                  <a href="mailto:contact@nouzhapalace.com" className="inline-flex items-center text-sm px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors">
                    <Mail className="w-4 h-4 mr-2" /> contact@nouzhapalace.com
                  </a>
                </div>
              </div>
            </div>
            
            {/* Bouton "Voir mes réservations" pour utilisateurs connectés */}
            {currentUser && (
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex justify-center sm:justify-start mb-3 sm:mb-0 sm:mr-4">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                    <Clock className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="font-medium text-gray-800">{t.viewMyBookings || 'Voir toutes mes réservations'}</h3>
                  <p className="text-gray-600 mb-3">{t.manageBookings || 'Accédez à l\'historique complet de vos réservations pour les consulter ou les gérer.'}</p>
                  <Link 
                    to="/mon-compte/reservations" 
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    {t.viewAllBookings || 'Voir mes réservations'}
                  </Link>
                </div>
              </div>
            )}
            
            {/* Bouton pour demander une facture */}
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex justify-center sm:justify-start mb-3 sm:mb-0 sm:mr-4">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                  <FileText className="w-5 h-5" />
                </div>
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-medium text-gray-800">{t.requestInvoice || 'Demander une facture'}</h3>
                <p className="text-gray-600 mb-3">{t.invoiceInfo || 'Recevez une facture détaillée de votre réservation par email.'}</p>
                <Link 
                  to={`/invoice-request/${reservation?.reservationObjectId || reservation?.id}/${reservation?.serviceType || 'hotel'}`}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t.requestInvoiceButton || 'Demander ma facture'}
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Retour à l'accueil */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center text-orange-600 hover:text-orange-700 font-medium px-6 py-2 rounded-xl hover:bg-orange-50 transition-all">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t.backToHome || 'Retour à l\'accueil'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReservationSuccessPage;